import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import NowPageContent from './NowPageContent';
import { SessionType } from '../../types/entities';
import CMX from '../../api';
import { isUserLogged } from '../../reducers';
import RequireAuthMessage from '../user/RequireAuthMessage';
import { getUserFavoriteCinemasIds } from '../../reducers/user';
import NoContentMessage from '../common/NoContentMessage';
import Button from '../Button';
import FullColumnLayout from '../FullColumnLayout';
import MDSpinner from 'react-md-spinner';
import FavCinemasAddRemove from '../user/FavCinemasAddRemove';
import NowPageSidebar from './NowPageSidebar';
import BottomPanel from '../BottomPanel';

interface Props {
  favCinemasIds: Array<number>;
  isUserLogged: boolean;
  match: {
    params: {
      time_limit?: string;
    }
  };
}

const NowPage: React.FC<Props> = (props) => {
  const defaultTimeLimit = 60;
  const baseUrl = '/por-comenzar';
  const [timeLimit, setTimeLimit] = React.useState<number | null>(null);
  const [sessions, setSessions] = React.useState<Array<SessionType> | null>(null);
  const [showFavCinemasEdit, setShowFavCinemasEdit] = React.useState(false);
  const [showTimeSelector, setShowTimeSelector] = React.useState(false);
  const lastFetchedFavCinemasIds = React.useRef<string>('');
  const previousShowFavCinemasEdit = React.useRef<boolean>(false);

  const getFavCinemasIdsKey = () => JSON.stringify([...(props.favCinemasIds || [])].sort((a, b) => a - b));

  useEffect(() => {
    const urlTimeLimit = props.match.params.time_limit ? parseInt(props.match.params.time_limit.split('-')[1], 10) : defaultTimeLimit;
    setTimeLimit(urlTimeLimit);
  }, [props.match.params.time_limit]);

  useEffect(() => {
    fetch();
  }, [timeLimit, props.isUserLogged]);

  useEffect(() => {
    const favCinemasEditClosed = previousShowFavCinemasEdit.current && !showFavCinemasEdit;
    const favCinemasChangedSinceLastFetch = lastFetchedFavCinemasIds.current !== getFavCinemasIdsKey();

    if (favCinemasEditClosed && favCinemasChangedSinceLastFetch) {
      fetch();
    }

    previousShowFavCinemasEdit.current = showFavCinemasEdit;
  }, [showFavCinemasEdit, props.favCinemasIds]);

  const fetch = () => {
    setSessions(null);

    if (!props.isUserLogged || !timeLimit) {
      return;
    }

    lastFetchedFavCinemasIds.current = getFavCinemasIdsKey();

    CMX.get('/me/cinemas/sessions/now', { time_limit: timeLimit })
      .then((response: any) => {
        setSessions(response.data);
      });
  }

  return (
    <>
      <FullColumnLayout>
        <Content baseUrl={baseUrl}
          timeLimit={timeLimit}
          sessions={sessions}
          onCinemasEdit={() => setShowFavCinemasEdit(true)}
          onTimeEdit={() => setShowTimeSelector(true)}
          {...props}
        />
      </FullColumnLayout>

      {showFavCinemasEdit && <FavCinemasAddRemove onClose={() => setShowFavCinemasEdit(false)} />}
      {showTimeSelector && <BottomPanel open onClose={() => setShowTimeSelector(false)}>
        <NowPageSidebar onChange={() => setShowTimeSelector(false)} baseUrl={baseUrl} timeLimit={timeLimit} />
      </BottomPanel>}
    </>
  );
}

const Content = (props) => {
  if (!props.isUserLogged) {
    return <RequireAuthMessage icon='fa-clock'>
      <p>Inicia sesión para guardar tus cines y ver qué está por comenzar.</p>
    </RequireAuthMessage>;
  }

  if (!props.favCinemasIds || props.favCinemasIds.length === 0) {
    return <NoContentMessage icon='fa-clock' title='Aún no tienes cines en "Mis cines"'>
      <p>Agrega tus favoritos y ve las funciones que están por comenzar.</p>
      <Button block onClick={props.onCinemasEdit}>Agregar cines</Button>
    </NoContentMessage>;
  }

  if (props.sessions === null) {
    return <div className="text-center mb-5"><MDSpinner singleColor="var(--primary)" /></div>;
  }
  
  if (!props.sessions.length) {
    return <NoContentMessage icon='fal fa-clock'>
      <p className='my-5'>No hay funciones próximas a comenzar en tus cines favoritos en los próximos <strong>{props.timeLimit} minutos.</strong></p>
      <Button block onClick={props.onCinemasEdit} className='mb-3'>Agregar cines</Button>
      <Button block primary onClick={props.onTimeEdit}>Configurar tiempo</Button>
    </NoContentMessage>;
  }

  return <NowPageContent {...props} />;
}

const mapStateToProps = (state) => {
  return {
    isUserLogged: isUserLogged(state),
    favCinemasIds: getUserFavoriteCinemasIds(state)
  };
}

export default connect(mapStateToProps)(NowPage);